import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Wind,
  Droplets,
  Hand,
  CalendarCheck,
  Square,
  Waves,
  ShieldCheck,
  Leaf,
  Eye,
  CalendarClock,
  Wrench,
  BadgeCheck,
  CheckCircle2,
  ArrowRight,
  Phone,
  ChevronDown,
  MapPin,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";

const SITE_URL = "https://deepcleanrr.com";

const FOAM_INCLUDES = [
  "Toilet & urinal deep cleaning",
  "Sink & faucet sanitation",
  "Wall & partition cleaning",
  "Countertop disinfection",
  "Floor sanitation",
  "High-touch surface disinfecting",
  "Odor-causing bacteria removal",
];

const DISINFECT_ITEMS = [
  { icon: Droplets, label: "Toilet & urinal disinfection" },
  { icon: Hand, label: "Sink & faucet sanitization" },
  { icon: ShieldCheck, label: "Door handle & partition cleaning" },
  { icon: Sparkles, label: "Countertop disinfection" },
  { icon: Square, label: "Mirror polishing & streak removal" },
  { icon: BadgeCheck, label: "Touchpoint sanitation" },
];

const ODOR_SOURCES = [
  "Urine buildup",
  "Mold & mildew",
  "Drain bacteria",
  "Moisture accumulation",
  "High-traffic restroom use",
];

const INDUSTRIES = [
  "Office buildings",
  "Medical offices",
  "Dental clinics",
  "Retail stores",
  "Restaurants",
  "Shopping centers",
  "Warehouses",
  "Manufacturing facilities",
  "Schools & universities",
  "Gyms & fitness centers",
  "Government facilities",
  "Property management",
];

const SERVICE_AREAS = [
  "Wilmington",
  "Wrightsville Beach",
  "Carolina Beach",
  "Kure Beach",
  "Surf City",
  "Topsail Beach",
  "Leland",
  "Belville",
  "Hampstead",
  "Ogden",
  "Castle Hayne",
  "Porters Neck",
  "Rocky Point",
  "Sneads Ferry",
  "Wallace",
  "Burgaw",
  "Downtown Wilmington",
  "South Wilmington",
  "Wrightsboro",
  "Masonboro",
];

const WHY = [
  {
    icon: Waves,
    title: "Specialized Deep Foam Technology",
    desc: "Advanced foam methods break down grime, bacteria, and odor buildup more effectively than standard cleaning.",
  },
  {
    icon: Wrench,
    title: "Experienced Professionals",
    desc: "Trained technicians follow detailed sanitation procedures for thorough, consistent results.",
  },
  {
    icon: ShieldCheck,
    title: "Hospital-Grade Disinfectants",
    desc: "Commercial-grade products effective against germs, bacteria, and viruses — safe for commercial spaces.",
  },
  {
    icon: CalendarClock,
    title: "Flexible Schedules",
    desc: "Daily, weekly, nightly, one-time, or emergency cleaning — built around your operation.",
  },
  {
    icon: Eye,
    title: "Customized Cleaning Plans",
    desc: "Plans tailored to your building size, traffic volume, and industry-specific requirements.",
  },
  {
    icon: Leaf,
    title: "Eco-Friendly Options",
    desc: "Effective, environmentally responsible solutions safe for employees and visitors.",
  },
];

const BENEFITS = [
  "Improve workplace hygiene",
  "Reduce bacteria & germ buildup",
  "Create a better customer experience",
  "Minimize restroom odors",
  "Maintain a professional appearance",
  "Support employee health & safety",
  "Meet sanitation standards",
  "Create a cleaner, healthier environment",
];

const PROCESS = [
  {
    n: "01",
    title: "Initial Inspection",
    desc: "We evaluate restroom conditions, traffic levels, and sanitation needs to develop the right cleaning approach.",
  },
  {
    n: "02",
    title: "Deep Foam Cleaning & Sanitization",
    desc: "Our technicians apply specialized foam solutions that break down grime and bacteria while thoroughly sanitizing every surface.",
  },
  {
    n: "03",
    title: "Odor Treatment",
    desc: "We eliminate odors at the source using advanced deodorization methods — not just mask them.",
  },
  {
    n: "04",
    title: "Final Quality Check",
    desc: "Before completing service, we inspect every area to ensure it meets our high cleanliness standards.",
  },
];

const FAQS = [
  {
    q: "How often should commercial restrooms be professionally cleaned?",
    a: "High-traffic commercial restrooms should typically be cleaned daily, with periodic deep cleaning services scheduled weekly or monthly depending on usage.",
  },
  {
    q: "What is deep foam cleaning?",
    a: "Deep foam cleaning is a specialized sanitation method that uses thick cleaning foam to penetrate surfaces, break down buildup, and disinfect hard-to-clean restroom areas more effectively than standard cleaning.",
  },
  {
    q: "Do you use eco-friendly cleaning products?",
    a: "Yes. We offer environmentally friendly restroom cleaning solutions that are effective and safe for employees and visitors.",
  },
  {
    q: "Can you clean restrooms after business hours?",
    a: "Absolutely. We provide flexible scheduling, including evenings, weekends, and overnight cleaning services.",
  },
  {
    q: "Do you offer recurring janitorial services?",
    a: "Yes. We provide ongoing restroom sanitation and commercial janitorial cleaning programs tailored to your facility.",
  },
];

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/ehk68r2n_Commercial%20Cleaning.png";
const FOAM_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/evxvi30p_Commercial%20Restroom%20Clean.png";
const CREW_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/kjlagz71_Commerical%20Restroom%20Clean%201.png";
const DROP_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/evxvi30p_Commercial%20Restroom%20Clean.png";

const Faq = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      data-testid={`faq-item-${idx}`}
      className="border-b border-slate-200 py-5"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${idx}`}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="font-heading text-lg font-bold text-slate-900 md:text-xl">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={2}
          className={`shrink-0 text-blue-600 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="mt-3 font-body text-base font-medium leading-relaxed text-slate-600">
          {a}
        </p>
      )}
    </div>
  );
};

const ServicesPage = () => {
  return (
    <main data-testid="services-page" className="bg-white">
      <SEO
        title="Commercial Restroom Cleaning Wilmington, NC | Deep Clean"
        description="Deep foam cleaning, sanitization & disinfection, odor removal, and maintenance programs for high-traffic commercial restrooms in Wilmington, NC."
        canonical={`${SITE_URL}/services/wilmington`}
        image="https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/ehk68r2n_Commercial%20Cleaning.png"
        type="website"
        keywords={[
          "commercial restroom cleaning Wilmington",
          "deep foam cleaning",
          "Wilmington janitorial services",
          "restroom sanitation",
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-slate-950 pt-24">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          aria-hidden="true"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div
          className="absolute inset-0 -z-10"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,9,19,0.92) 0%, rgba(5,9,19,0.70) 30%, rgba(5,9,19,0.55) 100%), linear-gradient(90deg, rgba(5,9,19,0.78) 0%, rgba(5,9,19,0.45) 60%, rgba(5,9,19,0.15) 100%), radial-gradient(60% 50% at 85% 20%, rgba(6,182,212,0.25), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-16 md:px-12 md:pb-32 md:pt-24">
          <p
            data-testid="services-hero-eyebrow"
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur"
          >
            Wilmington, NC · Commercial Restroom Sanitation
          </p>
          <h1
            data-testid="services-hero-heading"
            className="max-w-4xl font-heading text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-8xl"
          >
            Commercial Restroom
            <br />
            <span className="text-cyan-300">Sanitation Cleaning</span>
            <br />
            in Wilmington.
          </h1>
          <p
            data-testid="services-hero-subtitle"
            className="mt-8 max-w-3xl font-body text-base font-medium leading-relaxed text-slate-200 md:text-lg"
          >
            Deep cleaning solutions for offices, retail, medical, restaurants,
            schools, warehouses, and industrial properties. Hospital-grade
            disinfectants, deep-foam technology, and detail-oriented technicians
            — built to keep your business clean, compliant, and welcoming.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="services-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white transition-colors hover:bg-blue-700"
            >
              Request a Quote
              <ArrowRight
                size={18}
                strokeWidth={2.2}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="services-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white transition-colors hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              Call 910-236-9971
            </a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="border-b border-slate-200 bg-white py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:grid-cols-12 md:gap-16 md:px-12">
          <div className="md:col-span-5">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Overview
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Professional commercial
              <br />
              <span className="text-slate-400">restroom cleaning.</span>
            </h2>
          </div>
          <div className="md:col-span-7">
            <div className="space-y-5 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              <p>
                Keeping your commercial restrooms clean, sanitized, and
                odor-free is essential for maintaining a healthy workplace and
                creating a positive impression on customers and employees.
              </p>
              <p>
                We specialize in <strong>deep foam cleaning</strong>,{" "}
                <strong>high-touch surface disinfection</strong>,{" "}
                <strong>restroom deep cleaning</strong>,{" "}
                <strong>odor elimination</strong>, and{" "}
                <strong>commercial bathroom sanitation</strong> using
                hospital-grade products and advanced methods.
              </p>
              <p>
                Whether you need one-time restroom deep cleaning or recurring
                janitorial sanitation services, our Wilmington restroom cleaning
                professionals deliver reliable, detail-oriented results that
                help your business stay clean, compliant, and welcoming.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DEEP FOAM CLEANING */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-md border border-slate-200">
              <img
                src={FOAM_IMG}
                alt="Deep foam cleaning of restroom tile and grout"
                data-testid="foam-image"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Signature Service
            </p>
            <h2
              data-testid="foam-heading"
              className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
            >
              Deep Foam Restroom Cleaning
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Our specialized foam process penetrates and lifts away dirt,
              bacteria, grime, and buildup from restroom surfaces. Foam cleaning
              reaches areas traditional methods often miss — especially
              effective for high-traffic restrooms where surface cleaning isn't
              enough.
            </p>
            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {FOAM_INCLUDES.map((f, i) => (
                <li
                  key={f}
                  data-testid={`foam-item-${i}`}
                  className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3"
                >
                  <CheckCircle2
                    size={18}
                    strokeWidth={2}
                    className="mt-0.5 shrink-0 text-blue-600"
                  />
                  <span className="font-body text-sm font-semibold text-slate-800">
                    {f}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SANITIZING & DISINFECTION + ODOR */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Sanitizing & Disinfection
            </p>
            <h2
              data-testid="disinfect-heading"
              className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"
            >
              Every fixture. Every touchpoint.
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2">
              {DISINFECT_ITEMS.map((d, i) => (
                <div
                  key={d.label}
                  data-testid={`disinfect-${i}`}
                  className="group flex items-start gap-4 bg-white p-5 transition-colors hover:bg-slate-50"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-blue-600/10 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                    <d.icon size={18} strokeWidth={1.6} />
                  </span>
                  <span className="font-body text-sm font-semibold leading-snug text-slate-800">
                    {d.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-md border border-slate-200 bg-slate-950 p-8 text-white md:p-10">
              <div
                className="absolute inset-0 opacity-50"
                aria-hidden="true"
                style={{
                  backgroundImage: `url(${DROP_IMG})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  mixBlendMode: "overlay",
                }}
              />
              <div
                className="absolute inset-0"
                aria-hidden="true"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(5,9,19,0.75) 0%, rgba(5,9,19,0.92) 100%), radial-gradient(60% 50% at 80% 0%, rgba(6,182,212,0.30), transparent 70%)",
                }}
              />
              <div className="relative">
                <p className="font-body text-[10px] font-semibold uppercase tracking-[0.28em] text-cyan-300">
                  — Odor Removal & Deodorization
                </p>
                <h3 className="mt-4 font-heading text-3xl font-black tracking-tighter text-white">
                  We don't mask odors. We eliminate them.
                </h3>
                <p className="mt-5 font-body text-sm leading-relaxed text-slate-300 md:text-base">
                  We treat odor at the source — for results that actually last.
                  Common sources we eliminate:
                </p>
                <ul className="mt-6 space-y-2.5">
                  {ODOR_SOURCES.map((o, i) => (
                    <li
                      key={o}
                      data-testid={`odor-${i}`}
                      className="flex items-start gap-2.5 font-body text-sm font-semibold text-white"
                    >
                      <CheckCircle2
                        size={16}
                        strokeWidth={2}
                        className="mt-0.5 shrink-0 text-cyan-300"
                      />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — Why Wilmington Businesses Choose Us
              </p>
              <h2
                data-testid="why-services-heading"
                className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
              >
                Six reasons we're trusted across Wilmington.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                Specialized technology, trained crews, and flexible plans — all
                tailored to your facility.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((r, i) => (
              <div
                key={r.title}
                data-testid={`why-service-${i}`}
                className="group bg-white p-8 transition-colors hover:bg-slate-950"
              >
                <span className="grid h-12 w-12 place-items-center rounded-md bg-blue-600/10 text-blue-600 transition-colors group-hover:bg-cyan-300/15 group-hover:text-cyan-300">
                  <r.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-slate-900 transition-colors group-hover:text-white lg:text-xl">
                  {r.title}
                </h3>
                <p className="mt-3 font-body text-sm font-medium leading-relaxed text-slate-600 transition-colors group-hover:text-white/80">
                  {r.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES SERVED */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Industries We Serve in Wilmington
            </p>
            <h2
              data-testid="industries-services-heading"
              className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
            >
              From corner offices to manufacturing floors.
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {INDUSTRIES.map((ind, i) => (
              <div
                key={ind}
                data-testid={`served-industry-${i}`}
                className="group flex items-center gap-3 rounded-md border border-slate-200 bg-white p-4 transition-colors hover:border-blue-600"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-blue-600/10 text-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                  <CheckCircle2 size={16} strokeWidth={2} />
                </span>
                <span className="font-body text-sm font-semibold text-slate-800">
                  {ind}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="border-t border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — Service Areas
              </p>
              <h2
                data-testid="service-areas-heading"
                className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
              >
                Serving Wilmington & the surrounding Cape Fear Region.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                Based in Wilmington, NC — we proudly service businesses across
                New Hanover, Pender, and Brunswick counties. Don't see your
                area?{" "}
                <a
                  href="tel:9102369971"
                  className="font-bold text-blue-600 hover:underline"
                >
                  Give us a call
                </a>
                .
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {SERVICE_AREAS.map((area, i) => (
              <div
                key={area}
                data-testid={`service-area-${i}`}
                className="group flex items-center gap-2.5 rounded-md border border-slate-200 bg-white p-3 transition-colors hover:border-blue-600"
              >
                <MapPin
                  size={14}
                  strokeWidth={2}
                  className="shrink-0 text-blue-600 transition-colors group-hover:text-blue-700"
                />
                <span className="font-body text-[13px] font-semibold text-slate-800">
                  {area}
                </span>
              </div>
            ))}
          </div>

          {/* Dedicated location pages */}
          <div className="mt-10">
            <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
              Location pages
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Link
                to="/services/wrightsville-beach"
                data-testid="link-wrightsville-beach-service-page"
                className="group inline-flex items-center gap-2 rounded-md border border-blue-600 bg-blue-600/5 px-4 py-2.5 font-body text-sm font-bold text-blue-700 transition-colors hover:bg-blue-600 hover:text-white"
              >
                <MapPin size={14} strokeWidth={2.2} />
                Wrightsville Beach Restroom Cleaning
                <ArrowRight
                  size={14}
                  strokeWidth={2.2}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS + CREW IMAGE */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Our Process
            </p>
            <h2
              data-testid="process-heading"
              className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
            >
              Four steps to a spotless restroom.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Methodical, repeatable, and tailored to your space — every
              service follows the same proven sequence.
            </p>
            <div className="mt-8 overflow-hidden rounded-md border border-slate-200">
              <img
                src={CREW_IMG}
                alt="Commercial cleaning crew at work"
                data-testid="crew-image"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <ol className="space-y-4">
              {PROCESS.map((p) => (
                <li
                  key={p.n}
                  data-testid={`process-step-${p.n}`}
                  className="group flex items-start gap-5 rounded-md border border-slate-200 bg-white p-6 transition-colors hover:border-blue-600 md:p-8"
                >
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-blue-600 font-heading text-base font-black tabular-nums text-white">
                    {p.n}
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 md:text-xl">
                      {p.title}
                    </h3>
                    <p className="mt-2 font-body text-sm font-medium leading-relaxed text-slate-600 md:text-base">
                      {p.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* BENEFITS STRIP */}
      <section className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Benefits
            </p>
            <h2
              data-testid="benefits-heading"
              className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"
            >
              What you get with professional sanitation.
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b, i) => (
              <div
                key={b}
                data-testid={`benefit-${i}`}
                className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-5"
              >
                <Sparkles size={18} strokeWidth={1.6} className="mt-0.5 shrink-0 text-blue-600" />
                <span className="font-body text-sm font-semibold leading-snug text-slate-800">
                  {b}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — FAQ
            </p>
            <h2
              data-testid="faq-heading"
              className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
            >
              Frequently Asked Questions.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Have a different question? Call us at{" "}
              <a
                href="tel:9102369971"
                className="font-bold text-blue-600 hover:underline"
              >
                910-236-9971
              </a>
              .
            </p>
          </div>
          <div className="lg:col-span-8">
            {FAQS.map((f, i) => (
              <Faq key={f.q} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-slate-950 py-20 text-white md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(50% 40% at 0% 100%, rgba(0,85,255,0.40), transparent)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 md:px-12 lg:flex-row lg:items-center">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              — Request Service in Wilmington
            </p>
            <h2
              data-testid="cta-heading"
              className="mt-4 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl lg:text-5xl"
            >
              Ready for restrooms your customers will notice — for the right
              reasons?
            </h2>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="cta-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white transition-colors hover:bg-blue-700"
            >
              Book a Cleaning
              <ArrowRight
                size={18}
                strokeWidth={2.2}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="cta-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white transition-colors hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              910-236-9971
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ServicesPage;
